import React, { useEffect, useRef } from 'react';

type Theme =
  | 'bubblegum' | 'neon-glow' | 'tropical' | 'candy' | 'space' | 'retro'
  | 'halloween' | 'christmas' | 'valentine';

interface FloatingBubblesProps {
  theme?: Theme;
  density?: number;
}

const themeEmojis: Record<Theme, string[]> = {
  bubblegum: ['🫧', '💖', '🌸', '🦄'],
  'neon-glow': ['✨', '🌃', '💜', '🔮'],
  tropical: ['🌴', '🌺', '🍹', '🦜'],
  candy: ['🍭', '🍬', '🧁', '🍓'],
  space: ['🚀', '🌌', '🪐', '⭐'],
  retro: ['🕹️', '👾', '🎮', '🟪'],
  halloween: ['🎃', '👻', '🦇', '🕷️'],
  christmas: ['🎄', '🎅', '❄️', '🎁'],
  valentine: ['💖', '🌹', '💌', '🕊️'],
};

export const FloatingBubbles: React.FC<FloatingBubblesProps> = ({ 
  theme = 'bubblegum', 
  density = 12 
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    let width = canvas.offsetWidth;
    let height = canvas.offsetHeight;
    canvas.width = width;
    canvas.height = height;

    const emojis = themeEmojis[theme] || themeEmojis.bubblegum;
    const count = Math.max(6, Math.min(30, density || 12));

    const particles: Array<{
      x: number;
      y: number;
      emoji: string;
      size: number;
      speed: number;
      drift: number;
      alpha: number;
    }> = [];

    for (let i = 0; i < count; i++) {
      particles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        emoji: emojis[Math.floor(Math.random() * emojis.length)],
        size: Math.random() * 26 + 28,
        speed: Math.random() * 0.4 + 0.25,
        drift: (Math.random() - 0.5) * 0.4,
        alpha: Math.random() * 0.12 + 0.88,
      });
    }

    let raf: number;

    const draw = () => {
      ctx.clearRect(0, 0, width, height);

      for (let p of particles) {
        ctx.save();
        ctx.globalAlpha = p.alpha;
        ctx.font = `${p.size}px serif`;
        ctx.textAlign = 'center';
        ctx.fillText(p.emoji, p.x, p.y);
        ctx.restore();

        p.y -= p.speed;
        p.x += p.drift;

        if (p.y < -30) {
          p.y = height + 20;
          p.x = Math.random() * width;
          p.emoji = emojis[Math.floor(Math.random() * emojis.length)];
        }

        if (p.x < -20) p.x = width + 20;
        if (p.x > width + 20) p.x = -20;
      }

      raf = requestAnimationFrame(draw);
    };

    const onResize = () => {
      width = canvas.offsetWidth;
      height = canvas.offsetHeight;
      canvas.width = width;
      canvas.height = height;
    };

    window.addEventListener('resize', onResize);
    draw();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
    };
  }, [theme, density]);

  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none z-0" />;
};
